import React, { useState, useEffect } from 'react';
import api from '../lib/api';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

const DashboardPage = () => {
  const [stats, setStats] = useState({
    total_clients: 0,
    active_clients: 0,
    total_sessions: 0,
    total_vouchers: 0,
    cabin_minutes: {},
    recent_clients: [],
    recent_sessions: [],
    recent_vouchers: []
  });
  const [clients, setClients] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsResponse, clientsResponse] = await Promise.all([
          api.get('/stats'),
          api.get('/clients')
        ]);
        setStats(statsResponse.data);
        setClients(clientsResponse.data);
      } catch (error) {
        console.error("Error fetching dashboard data", error);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-stone-800">Dashboard</h1>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        <div className="bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-stone-100 flex flex-col">
          <span className="text-stone-500 text-sm md:text-base font-medium mb-1 md:mb-2">Total Clientes</span>
          <span className="text-3xl md:text-4xl font-bold text-stone-800">{stats.total_clients}</span>
        </div>
        <div className="bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-stone-100 flex flex-col">
          <span className="text-stone-500 text-sm md:text-base font-medium mb-1 md:mb-2">Total Sesiones</span>
          <span className="text-3xl md:text-4xl font-bold text-teal-600">{stats.total_sessions}</span>
        </div>
        <div className="bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-stone-100 flex flex-col">
          <span className="text-stone-500 text-sm md:text-base font-medium mb-1 md:mb-2">Total Bonos</span>
          <span className="text-3xl md:text-4xl font-bold text-rose-600">{stats.total_vouchers}</span>
        </div>
      </div>

      <div className="mt-8">
        <h2 className="text-xl font-bold text-stone-800 mb-4">Uso de Cabinas (Minutos)</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {Object.entries(stats.cabin_minutes || {}).map(([cabin, minutes]) => (
            <div key={cabin} className="bg-stone-50 p-3 md:p-4 rounded-xl border border-stone-200 flex items-center justify-between">
              <span className="font-semibold text-sm md:text-base text-stone-700">{cabin}</span>
              <span className="text-amber-600 font-bold text-sm md:text-base">{minutes} min</span>
            </div>
          ))}
          {Object.keys(stats.cabin_minutes || {}).length === 0 && (
            <div className="text-stone-500 col-span-full p-4 border border-dashed border-stone-200 rounded-xl">
              No hay datos de cabinas disponibles.
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
          <div className="p-4 border-b border-stone-100 bg-stone-50">
            <h3 className="font-bold text-stone-800">Clientes Recientes</h3>
          </div>
          <div className="p-4 space-y-3">
            {(stats.recent_clients || []).map(c => (
              <div key={c.id} className="flex justify-between items-center border-b border-stone-50 pb-2 last:border-0 last:pb-0">
                <span className="font-medium text-stone-700">{c.name}</span>
                <span className="text-sm text-stone-400">{c.phone || c.email || '-'}</span>
              </div>
            ))}
            {(!stats.recent_clients || stats.recent_clients.length === 0) && (
              <div className="text-stone-400 text-sm italic">Sin clientes recientes</div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
          <div className="p-4 border-b border-stone-100 bg-stone-50">
            <h3 className="font-bold text-stone-800">Sesiones Recientes</h3>
          </div>
          <div className="p-4 space-y-3">
            {(stats.recent_sessions || []).map(s => {
              const cName = clients.find(c => c.id === s.client_id)?.name || 'Cliente Eliminado';
              return (
                <div key={s.id} className="flex justify-between items-center border-b border-stone-50 pb-2 last:border-0 last:pb-0">
                  <div>
                    <div className="font-medium text-stone-700">{cName}</div>
                    <div className="text-xs text-stone-400">{formatDate(s.date)} {s.time} • C: {s.cabin || '-'}</div>
                  </div>
                  <span className="text-sm font-bold text-teal-600">-{s.minutes} min</span>
                </div>
              );
            })}
            {(!stats.recent_sessions || stats.recent_sessions.length === 0) && (
              <div className="text-stone-400 text-sm italic">Sin sesiones recientes</div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
          <div className="p-4 border-b border-stone-100 bg-stone-50">
            <h3 className="font-bold text-stone-800">Bonos Recientes</h3>
          </div>
          <div className="p-4 space-y-3">
            {(stats.recent_vouchers || []).map(v => {
              const cName = clients.find(c => c.id === v.client_id)?.name || 'Cliente Eliminado';
              return (
                <div key={v.id} className="flex justify-between items-center border-b border-stone-50 pb-2 last:border-0 last:pb-0">
                  <div>
                    <div className="font-medium text-stone-700">{cName}</div>
                    <div className="text-xs text-stone-400">{formatDate(v.purchase_date)}</div>
                  </div>
                  <span className="text-sm font-bold text-rose-600">+{v.minutes} min</span>
                </div>
              );
            })}
            {(!stats.recent_vouchers || stats.recent_vouchers.length === 0) && (
              <div className="text-stone-400 text-sm italic">Sin bonos recientes</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
